import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Copy, Check } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [resetUrl, setResetUrl] = useState(null);
    const [copied, setCopied] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            if (data.token) {
                const url = `${window.location.origin}/reset-password/${data.token}`;
                setResetUrl(url);
                toast.success("Reset link generated");
            } else {
                toast.success("If that email is registered, a reset link has been generated");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(resetUrl);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="forgot-page">
            <div className="surface p-8 w-full max-w-md">
                <div className="flex items-center gap-2 mb-6">
                    <KeyRound className="w-4 h-4 text-accent" />
                    <span className="overline !text-zinc-400">Password reset</span>
                </div>
                <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Forgot password?</h1>
                <p className="text-sm text-zinc-500 mb-8">Enter your email — we&apos;ll generate a reset link.</p>

                {!resetUrl ? (
                    <form onSubmit={submit} method="post" className="space-y-5" autoComplete="on">
                        <div>
                            <label htmlFor="forgot-email" className="block text-xs font-medium text-zinc-400 mb-2">Email</label>
                            <input
                                id="forgot-email"
                                name="email"
                                type="email"
                                autoComplete="username"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="surface-input"
                                data-testid="forgot-email"
                                placeholder="seller@store.com"
                            />
                        </div>
                        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="forgot-submit">
                            {loading ? "Generating..." : "Generate reset link"}
                        </button>
                    </form>
                ) : (
                    <div className="space-y-4" data-testid="forgot-result">
                        <div className="rounded-md border border-accent/40 bg-accent/5 p-4">
                            <div className="overline mb-2">Reset link (valid for 30 min)</div>
                            <div className="font-mono text-xs text-zinc-200 break-all mb-3" data-testid="forgot-reset-url">{resetUrl}</div>
                            <div className="flex gap-2">
                                <button onClick={copyLink} className="btn-ghost !py-1.5 !px-3 text-xs flex-1" data-testid="forgot-copy">
                                    {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy link</>}
                                </button>
                                <a href={resetUrl} className="btn-primary !py-1.5 !px-3 text-xs flex-1 text-center" data-testid="forgot-open">
                                    Open link
                                </a>
                            </div>
                        </div>
                        <p className="text-xs text-zinc-500">
                            🔧 Dev mode: The link is shown here directly. In production this would be emailed to the user.
                        </p>
                    </div>
                )}

                <p className="mt-6 text-sm text-zinc-500">
                    Remembered it? <Link to="/login" className="text-accent hover:underline" data-testid="forgot-back-login">Back to sign in</Link>
                </p>
            </div>
        </div>
    );
}
